import { Box, Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { InventoryProfileService } from "subModule/src/core/services";
import { GetInvoicesByPaymentIdResponse, PurchasePayments } from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import axios from "axios";

type PrintPaymentPayload = {
  payment: PurchasePayments | null;
  invoices: any[];
  error?: string;
};

/** Share one in-flight load across Strict Mode remounts so each API hits once. */
const printPaymentInflight = new Map<string, Promise<PrintPaymentPayload>>();

function loadPrintPurchasePayment(id: string): Promise<PrintPaymentPayload> {
  const existing = printPaymentInflight.get(id);
  if (existing) return existing;

  const promise = InventoryProfileService.getEntityById(id, "purchasepayment")
    .then(async (res: any) => {
      if (!res.success) {
        return {
          payment: null,
          invoices: [],
          error: res.message || "Something went wrong",
        };
      }
      const payment = (res.data as PurchasePayments) || null;
      const paymentId = res?.data?.id || 0;
      if (!paymentId) {
        return { payment, invoices: [] };
      }
      const invoicesRes: GetInvoicesByPaymentIdResponse =
        await InventoryProfileService.invoicesByPaymentId(paymentId);
      if (!invoicesRes.success) {
        return {
          payment,
          invoices: [],
          error: invoicesRes.message || "Something went wrong",
        };
      }
      return {
        payment,
        invoices: invoicesRes?.data?.invoices || [],
      };
    })
    .finally(() => {
      // Keep briefly so twin mounts in Strict Mode reuse; then allow refresh.
      window.setTimeout(() => printPaymentInflight.delete(id), 250);
    });

  printPaymentInflight.set(id, promise);
  return promise;
}

const PrintPurchasePayment: React.FC = () => {
    const params = useParams();
    const location = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const supplierId = (location.state as { supplier?: number | null })?.supplier;
    const handleBack = () => supplierId ? navigate(-1) : navigate('/inventory/supplier-payments');
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(true);
    const [payment, setPayment] = useState<PurchasePayments>();
    const [purchaseInvoices, setPurchaseInvoices] = useState<any[]>([]);

    useEffect(() => {
        const id = params?.id;
        if (!id) return;
        let active = true;

        loadPrintPurchasePayment(id)
            .then((payload) => {
                if (!active) return;
                if (payload.payment) setPayment(payload.payment);
                setPurchaseInvoices(payload.invoices);
                setLoading(false);
                if (payload.error) {
                    enqueueSnackbar(<AlertMessages text={payload.error} />, { variant: 'error' });
                }
            })
            .catch((err: any) => {
                if (!active || axios.isCancel(err)) return;
                console.error("===", err);
                setLoading(false);
                enqueueSnackbar(
                    <AlertMessages text={err?.data?.message || err?.message || "Something went wrong"} />,
                    { variant: 'error' },
                );
            });

        return () => {
            active = false;
        };
    }, [params?.id]);
    const columns = [
        { id: 'purchaseInvoiceNo', label: t('supplierBillNo'), numeric: false },
        { id: 'adjustedAmount', label: t('adjustedAmount'), numeric: false },
    ];
    return (
        loading ? <Loader /> : <Box>
            <Box className='max-w-[1100px] bg-white py-[10px] px-[20px] rounded-[8px] block mx-auto hide-on-print mb-4'>
                <Box className='flex justify-between items-center' id='form'>
                    <Typography className="font-Saira text-[12px] font-semibold" style={{ color: themeColors?.primary }}>{t('supplierPayment')}</Typography>
                    <Box className='flex gap-2'>
                        <Button className="secondary" variant="contained" style={{ color: themeColors?.primary }} disableElevation onClick={handleBack}>{t('back')}</Button>
                        <Button className="primary" variant="contained" style={{ background: themeColors?.primary }} disableElevation onClick={() => window.print()}>{t('Print')}</Button>
                    </Box>
                </Box>
            </Box>
            <Box id="print-root" className='max-w-[1100px] bg-white p-[20px] rounded-[8px] block mx-auto'>
                <Box className='relative flex justify-between mb-2 items-center'>
                    <Box>
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('Supplier')}: </span>
                            <span className="font-bold">{payment?.supplierName || ''}</span>
                        </Typography>
                        {payment?.accountTitle ? <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('ACCOUNT')}: </span>
                            <span className="font-bold">{payment?.accountTitle || ''}</span>
                        </Typography> : ''}
                    </Box>
                    {branding?.attributes?.logo ? <Box className="absolute left-1/2 -translate-x-[70%]">
                        <img src={branding?.attributes?.logo || ""}
                            style={{
                                height: "60px",
                                objectFit: "contain",
                            }}
                        />
                    </Box> : null}
                    <Box>
                        <Box className='block py-1 px-2 rounded-[8px]' style={{ background: themeColors?.appBg }}>
                            <Typography className="font-Saira text-[12px]">
                                <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('supplierPayment')} # </span>
                                <span className="font-semibold">{payment?.paymentNumber || ''}</span>
                            </Typography>
                        </Box>
                        <Typography className="font-Saira text-[12px] text-end font-semibold">
                            <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('referenceNo')} </span>
                            <span className="font-semibold">{payment?.referenceNumber || ''}</span>
                        </Typography>
                    </Box>
                </Box>
                <Box className='flex justify-between items-center py-3 px-2 rounded-[8px] mb-2' style={{ background: themeColors?.appBg }}>
                    <Typography className="font-Saira text-[12px] text-left">
                        <span className="italic font-medium">{t('PaymentMode')}: </span>
                        <span className="font-bold">{payment?.paymentModeTitle || ''}</span>
                    </Typography>
                    <Box className="flex gap-3 items-center">
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('adjustmentType')}</span>
                            <span>: </span>
                            <span className="font-bold">{[
                                    { value: 591, label: t("FIFO") },
                                    { value: 592, label: t("MANUAL") },
                                ].find((option: any) => option.value === payment?.adjustmentType)?.label || ''}
                            </span>
                        </Typography>
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('Payment') + ' ' + t('Date')}</span>
                            <span>: </span>
                            <span className="font-bold">{payment?.paymentDate?.split('T')[0] || ''}</span>
                        </Typography>
                        <Typography className="font-Saira text-[12px] text-left block py-1 px-3 rounded-[8px]" style={{ background: themeColors?.secondary + '33' }}>
                            <span className="italic font-medium">{t('recipentDate')}</span>
                            <span>: </span>
                            <span className="font-bold">{payment?.receiptDate?.split('T')[0] || ''}</span>
                        </Typography>
                    </Box>
                </Box>
                {purchaseInvoices && purchaseInvoices?.length ? <>
                    <Typography className="font-Saira text-[12px] font-semibold text-[#333843] text-left mb-2">{t('adjustedSupplierBills')}</Typography>
                    <Box className='w-[100%] mb-2' id="inventorytable">
                        <TableContainer>
                            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'small'}>
                                <TableHead>
                                    <TableRow sx={{ borderBottom: `1px solid #B8BDCB` }}>
                                        {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align={column.numeric ? 'right' : 'left'}
                                                padding={'normal'}
                                                sortDirection={false}
                                                className="font-bold text-center"
                                            >{column.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ '& tr:nth-child(odd)': { background: `transparent !important` }, '& tr': { borderBottom: `1px solid #B8BDCB` }, '& tr:last-child': { borderBottom: 0 } }}>
                                    {(purchaseInvoices || []).map((row, index) => {
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        return (
                                            <TableRow hover tabIndex={-1} key={row.id} sx={{ height: '36px' }}>
                                                {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                                    <TableCell
                                                        key={column.id}
                                                        align={column.numeric ? 'right' : 'left'}
                                                        padding={'normal'}
                                                        component={column.id === 'itemName' ? 'th' : undefined}
                                                        id={column.id === 'itemName' ? labelId : undefined}
                                                        scope={column.id === 'itemName' ? 'row' : undefined}
                                                        className="text-center"
                                                    >{column.id === 'receivedDate' || column.id === 'expiryDate'
                                                        ? (row as any)[column.id]?.split('T')[0]
                                                        : column.id === 'unitTitle' ? ((row as any)[column.id] || t('unit(s)'))
                                                        : column.id === 'total' ? 
                                                            <span className='font-bold'>{new Intl.NumberFormat('en-US', loggedInUserCurrency ? {
                                                                style: 'currency',
                                                                currency: loggedInUserCurrency || '',
                                                            } : {}).format(
                                                                ((row?.unitPrice || 0) * (row?.quantity || 0)) - (((row?.unitPrice || 0) * (row?.quantity || 0)) * ((row?.discount || 0)/100))
                                                            )}</span>
                                                        : (row as any)[column.id]
                                                        }</TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </> : null}
                <Box className='flex justify-end mt-4 mb-3'>
                    <Box className="w-[360px]">
                        <Divider className="bg-[#AEB3C4] mb-2" />
                        <Box className='flex justify-end mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left flex">
                                <span className="font-medium">{t('extraAmount')}: </span>
                                <p className="font-bold ms-1">
                                    <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                    <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format(payment?.balanceAmount || 0) || 0}</span>
                                </p>
                            </Typography>
                        </Box>
                        <Box className='flex justify-end mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left flex">
                                <span className="font-medium">{t('paidAmount')}: </span>
                                <p className="font-bold ms-1">
                                    <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                    <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format(payment?.amount || 0) || 0}</span>
                                </p>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {payment?.remarks ? <Box>
                    <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">{t("remarks")}</Typography>
                    <Typography className="font-Saira text-[12px] text-[#171826] text-left">{payment?.remarks}</Typography>
                </Box> : null}
            </Box>
        </Box>
    )
}

export default PrintPurchasePayment;